import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, DatePicker, Form, Image, Input, message, Modal, Typography, Upload } from 'antd';
import { DeleteOutlined, InboxOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import { shipmentApi, type ManualTrackEventResultDTO, type ShipmentDTO } from '../api/kfc';

const MAX_IMAGES = 5;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const MAX_TEXT = 500;
const ALLOWED_TYPES = ['image/png', 'image/jpeg'];

type PendingImage = { uid: string; file: File; url: string };

interface Props {
  shipment: ShipmentDTO | null;
  onClose: () => void;
  onSubmitted: (result: ManualTrackEventResultDTO) => void;
}

const ManualTrackEventModal: React.FC<Props> = ({ shipment, onClose, onSubmitted }) => {
  const [form] = Form.useForm<{ description?: string; eventTime?: Dayjs }>();
  const [images, setImages] = useState<PendingImage[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const imagesRef = useRef<PendingImage[]>([]);
  imagesRef.current = images;
  const open = !!shipment;

  const clearImages = useCallback(() => {
    imagesRef.current.forEach((img) => URL.revokeObjectURL(img.url));
    setImages([]);
  }, []);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      clearImages();
    }
  }, [open, form, clearImages]);

  const addFiles = useCallback((files: File[]) => {
    const current = imagesRef.current;
    const accepted: PendingImage[] = [];
    for (const f of files) {
      if (!ALLOWED_TYPES.includes(f.type) && !/\.(png|jpe?g)$/i.test(f.name || '')) {
        message.warning(`图片格式仅支持 png / jpg：${f.name || '图片'}`);
        continue;
      }
      if (f.size > MAX_IMAGE_BYTES) {
        message.warning(`单张图片不能超过 10MB：${f.name || '图片'}`);
        continue;
      }
      if (current.length + accepted.length >= MAX_IMAGES) {
        message.warning(`图片最多 ${MAX_IMAGES} 张`);
        break;
      }
      accepted.push({ uid: `${Date.now()}-${Math.random()}`, file: f, url: URL.createObjectURL(f) });
    }
    if (accepted.length) setImages([...current, ...accepted]);
  }, []);

  const removeImage = (uid: string) => {
    setImages((prev) => {
      const target = prev.find((p) => p.uid === uid);
      if (target) URL.revokeObjectURL(target.url);
      return prev.filter((p) => p.uid !== uid);
    });
  };

  const onOk = () => {
    if (!shipment) return;
    form.validateFields().then((values) => {
      const description = values.description?.trim() || '';
      if (!description && images.length === 0) {
        message.warning('请填写轨迹说明或上传图片依据');
        return;
      }
      const fd = new FormData();
      if (description) fd.append('description', description);
      if (values.eventTime) fd.append('eventTime', values.eventTime.format('YYYY-MM-DD HH:mm:ss'));
      images.forEach((img) => fd.append('files', img.file, img.file.name));
      setSubmitting(true);
      shipmentApi
        .addManualTrackEvent(shipment.shipmentId, fd)
        .then((res) => {
          message.success(res.data.statusAdvanced ? '已更新轨迹，运单状态已推进' : '已更新轨迹');
          onSubmitted(res.data);
        })
        .catch((e) => message.error(e.response?.data?.message || '操作失败'))
        .finally(() => setSubmitting(false));
    });
  };

  return (
    <Modal
      title={`更新轨迹 ${shipment?.shipmentId ?? ''}`}
      open={open}
      onCancel={onClose}
      onOk={onOk}
      okText="更新轨迹"
      confirmLoading={submitting}
      destroyOnClose
    >
      <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
        用于代理无法自动同步轨迹的运单。系统会按说明中的关键词前向推进状态（运输中 / 待派送 / 已签收）。
      </Typography.Paragraph>
      <Form form={form} layout="vertical">
        <Form.Item name="eventTime" label="轨迹时间" extra="不填则用当前时间">
          <DatePicker showTime style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="description" label="轨迹说明" extra={`文字或图片至少一项，最多 ${MAX_TEXT} 字`}>
          <Input.TextArea rows={3} maxLength={MAX_TEXT} placeholder="例如：货物已出库，运输中" />
        </Form.Item>
      </Form>
      <Upload.Dragger
        accept=".png,.jpg,.jpeg,image/png,image/jpeg"
        multiple
        showUploadList={false}
        beforeUpload={(file) => {
          addFiles([file]);
          return false;
        }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">上传依据图片（png / jpg，最多 5 张）</p>
      </Upload.Dragger>
      {images.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
          {images.map((img) => (
            <div key={img.uid} style={{ position: 'relative' }}>
              <Image src={img.url} width={72} height={72} style={{ objectFit: 'cover', borderRadius: 4 }} />
              <Button
                size="small"
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeImage(img.uid)}
                style={{ position: 'absolute', top: -6, right: -6 }}
              />
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

export default ManualTrackEventModal;
